import styles from './ListGalleryModule.module.scss';

import cx from 'classnames';
import React, { CSSProperties, FC, useRef } from 'react';

import {
  ListGalleryVariant,
  TextAlignment,
  type ListGalleryModule as ListGalleryModuleEntry,
} from 'api/contentful/contentTypes.cloverdotcom';
import { TextV2 } from 'components/TextV2/TextV2';
import { FlatEntry } from 'services/cms/flattenContentful';
import { AppType, toSysId } from 'services/cms/typesUtil';
import { ListGalleryItem } from './ListGalleryItem';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useListGalleryScroll } from './useListGalleryScroll';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';

type ListGalleryModuleProps = AppType.Component<FlatEntry<ListGalleryModuleEntry>>;

export const ListGalleryModule: FC<ListGalleryModuleProps> = ({
  _attributes,
  items: initialItems = [],
  textAlignment = TextAlignment.LEFT,
  title,
  variant = ListGalleryVariant.STATIC,
  headingTagName = 'h2',
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const { height } = useResizeObserver({ targetRef: titleRef });
  const { activeItemIndex, prevActiveItemIndex } = useListGalleryScroll(variant, rootRef.current);

  const items = initialItems.filter(Boolean);
  if (!items.length) return null;

  const isStatic = variant === ListGalleryVariant.STATIC;
  const isStickyScroll = variant === ListGalleryVariant.STICKY_SCROLL;
  return (
    <section
      ref={rootRef}
      className={cx(styles.root, {
        [styles.static]: isStatic,
        [styles.stickyScroll]: isStickyScroll,
        [styles.left]: textAlignment === TextAlignment.LEFT,
        [styles.center]: textAlignment === TextAlignment.CENTER,
        [styles.right]: textAlignment === TextAlignment.RIGHT,
      })}
      {..._attributes}
      style={{ '--title-height': `${height || 0}px` } as CSSProperties}
    >
      <div className={styles.container}>
        <div className={cx(isStickyScroll && styles.sticky)}>
          <div className={styles.titleWrapper} ref={titleRef}>
            <FadeToastUp>
              <TextV2
                className={styles.title}
                tagName={headingTagName}
                value={title}
                variant={isStickyScroll ? 'modern-h3' : 'modern-h2'}
              />
            </FadeToastUp>
          </div>
          {isStickyScroll && (
            <div className={styles.stickyImageItems}>
              {items.map((item, index) => {
                const { image, mobileImage, tabletImage, imagePosition } = item;
                return (
                  <ListGalleryItem
                    key={toSysId(item)}
                    item={{ image, mobileImage, tabletImage, imagePosition }}
                    className={cx(
                      activeItemIndex === index && styles.stickyActive,
                      prevActiveItemIndex === index && styles.stickyPrevActive,
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div className={cx(styles.items, isStickyScroll && styles.stickyTextItems)}>
          {items.map(item => {
            return (
              <ListGalleryItem
                key={toSysId(item)}
                item={isStickyScroll ? { ...item, image: undefined } : item}
              />
            );
          })}
        </div>
      </div>
      {isStickyScroll && <div className={styles.stickyBottom} />}
    </section>
  );
};
